import React, { useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TreeSelect } from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { fetchUnpaidTransactions } from '@store/Finance/AP/ApInvoices/UnpaidInvoicesSlice';
import { Card } from '@components/card/card';
import useGetUnApplyPrepayments from '@hooks/Finance/ApPayments/useGetUnApplyPrepayments';
import Loading from '@components/Shared/Loader/Loading';

export const StandardReceiptComponent: React.FC<{
  formik: any;
  onClose: () => void;
  OperatingUnitOptions: any[];
  treeCustomerData: any[];
}> = ({ formik, onClose, OperatingUnitOptions, treeCustomerData }) => {
  const { values, setFieldValue } = formik;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState<any[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<{ id: any; amount: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedTransactions.length > 0) {
      setSelectedTransactions([]);
      setTransactions([]);
      setFetchError(null);
    }
  }, [values.peopleId, values.organizationId]);

  const handleGetUnpaidTransactions = async () => {
    if (!values.organizationId || !values.peopleId) return;

    setIsLoading(true);
    setFetchError(null);

    try {
      const result = await dispatch(
        fetchUnpaidTransactions({
          peopleId: values.peopleId,
          organizationId: values.organizationId,
          TransactionType: 'Standard'
        })
      ).unwrap();

      if (result.succeeded) {
        const TransactionsWithType = result.data.map((invoice: any) => ({
          ...invoice,
          transactionType: 'Standard'
        }));
        console.log(JSON.stringify(result.data));

        setTransactions(TransactionsWithType);
        if (result.data.length === 0) {
          setFetchError('لم يتم العثور على فواتير غير مدفوعة');
        }
      } else {
        setFetchError('فشل في جلب الفواتير');
      }
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      setFetchError('فشل في جلب الفواتير');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (paymentScheduleId: any, remainingAmount: number) => {
    const isAlreadySelected = selectedTransactions.find(inv => inv.id === paymentScheduleId);

    if (isAlreadySelected) {
      setSelectedTransactions(selectedTransactions.filter(inv => inv.id !== paymentScheduleId));
    } else {
      setSelectedTransactions([...selectedTransactions, { id: paymentScheduleId, amount: remainingAmount }]);
    }
  };

  const handleAmountChange = (paymentScheduleId: any, amount: string) => {
    const numericAmount = parseFloat(amount) || 0;
    const transaction = transactions.find(inv => inv.arReceiptScheduleId === paymentScheduleId);

    if (transaction && numericAmount <= transaction.remainingAmount && numericAmount >= 0) {
      const updatedInvoices = selectedTransactions.map(inv =>
        inv.id === paymentScheduleId ? { ...inv, amount: numericAmount } : inv
      );
      setSelectedTransactions(updatedInvoices);
    }
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      const allInvoices = transactions.map(transaction => ({
        id: transaction.arReceiptScheduleId,
        amount: transaction.remainingAmount
      }));
      setSelectedTransactions(allInvoices);
    }
  };

  const handleSubmit = () => {
    const filteredTransactions = transactions
      .filter((f: any) => selectedTransactions.some(selected => selected.id === f.arReceiptScheduleId))
      .map((item: any) => {
        const selectedTransaction = selectedTransactions.find(selected => selected.id === item.arReceiptScheduleId);
        const receiptAmount = selectedTransaction?.amount || 0;
        return {
          id: null,
          arReceiptScheduleId: item.arReceiptScheduleId,
          transactionNum: item.transactionNum,
          transactionDate: item.transactionDate,
          dueDate: item.dueDate,
          transactionAmount: item.transactionAmount,
          schadualAmount: item.amount,
          // amount: item.remainingAmount,
          amount: receiptAmount,
          actualRemainingAmount: item.remainingAmount,
          transactionType: item.transactionType // ?
        };
      });
    formik?.setFieldValue('scheduleList', filteredTransactions);
    onClose();
  };

  // التحقق مما إذا كانت الفاتورة محددة
  const isTransactionSelected = (paymentScheduleId: any) => {
    return selectedTransactions.some(inv => inv.id === paymentScheduleId);
  };

  // الحصول على مبلغ الفاتورة المحددة
  const getSelectedTransactionAmount = (paymentScheduleId: any) => {
    const selectedTransaction = selectedTransactions.find(inv => inv.id === paymentScheduleId);
    return selectedTransaction?.amount || 0;
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });

  const totalSelectedAmount = selectedTransactions.reduce((sum, invoice) => sum + invoice.amount, 0);
  const isFormValid = selectedTransactions.length > 0 && selectedTransactions.every(invoice => invoice.amount > 0);
  const hasValidationErrors = selectedTransactions.some(invoice => invoice.amount === 0);

 
  return (
    <div>
      <div className='row g-3 mb-4'>
        <div className='col-lg-6'>
          <div className='form-group'>
            <label className='form-label fw-bold'>العميل</label>
            <TreeSelect
              showSearch
              className='w-100'
              treeLine
              placeholder='اختر العميل'
              allowClear
              treeDefaultExpandAll={false}
              value={values.peopleId}
              onChange={option => {
                setSelectedTransactions([]);
                setTransactions([]);
                formik.setFieldValue('peopleId', option === null ? null : option);
              }}
              treeData={treeCustomerData}
              treeNodeFilterProp='title'
              getPopupContainer={triggerNode => triggerNode.parentNode}
            />
          </div>
        </div>

        <div className='col-lg-4'>
          <div className='form-group'>
            <label className='form-label fw-bold'>الوحدة التشغيلية</label>
            <Select
              isSearchable={true}
              isClearable
              options={OperatingUnitOptions}
              value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId)}
              onChange={(option: any) => {
                setSelectedTransactions([]);
                setTransactions([]);
                setFieldValue('organizationId', option === null ? null : option.value);
              }}
              placeholder='اختر الوحدة التشغيلية'
              className='react-select-container'
              classNamePrefix='react-select'
            />
          </div>
        </div>

        <div className='col-lg-2'>
          <div className='form-group h-100 d-flex flex-column justify-content-end'>
            <button
              disabled={isLoading || !values.organizationId || !values.peopleId}
              type='button'
              className='btn btn-sm btn-outline-theme w-100 py-2'
              onClick={handleGetUnpaidTransactions}
            >
              {isLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  جاري التحميل...
                </>
              ) : (
                <>
                  <i className='fas fa-download me-2'></i>
                  جلب الفواتير
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {fetchError && (
        <div className='alert alert-warning d-flex align-items-center mb-4' role='alert'>
          <i className='fas fa-exclamation-triangle me-2'></i>
          <div>{fetchError}</div>
        </div>
      )}

      {/* {sortedInvoices.length > 0 && <PrepaymentsAlert peopleId={values.peopleId} />} */}

      {sortedTransactions.length > 0 && (
        <Card className='mb-4'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h6 className='mb-0'>
              <i className='fas fa-file-invoice me-2'></i>
              قائمة الفواتير غير المدفوعة
              <span className='badge bg-primary ms-2'>فواتير عادية</span>
            </h6>
            <div>
              <span className='badge bg-theme me-2'>الفواتير المحددة: {selectedTransactions.length}</span>
              <span className='badge bg-success'>الإجمالي: {totalSelectedAmount.toLocaleString()}</span>
            </div>
          </div>

          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table className='table table-hover mb-0'>
                <thead className='table-actived fw-bold'>
                  <tr>
                    <th className='text-center small'>
                      <div className='form-check'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={
                            selectedTransactions.length > 0 && selectedTransactions.length === transactions.length
                          }
                          onChange={handleSelectAll}
                          disabled={transactions.length === 0}
                        />
                      </div>
                    </th>

                    <th className='text-center small'>رقم السداد</th>
                    <th className='text-center small'>رقم الفاتورة</th>
                    <th className='text-center small'>تاريخ الفاتورة</th>
                    <th className='text-center small'>مبلغ الفاتورة</th>
                    <th className='text-center small'>المبلغ المتبقي</th>
                    <th className='text-center small'>المبلغ المراد دفعه</th>
                    <th className='text-center small'>تاريخ الاستحقاق</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransactions.map((item, index) => {
                    const isSelected = isTransactionSelected(item.arReceiptScheduleId);
                    const receiptAmount = getSelectedTransactionAmount(item.arReceiptScheduleId);

                    return (
                      <tr key={item.arReceiptScheduleId} className={isSelected ? 'table-active' : ''}>
                        <td className='text-center align-middle'>
                          <div className='form-check'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={isSelected}
                              onChange={() => handleCheckboxChange(item.arReceiptScheduleId, item.remainingAmount)}
                            />
                          </div>
                        </td>
                        <td className='text-center align-middle'>{item.receiptNum}</td>
                        <td className='text-center align-middle'>{item.transactionNum}</td>
                        <td className='text-center align-middle'>{item.transactionDate}</td>
                        <td className='text-center align-middle'>
                          <span className='fw-bold text-primary'>
                            {Number(item.transactionAmount).toLocaleString()}
                          </span>
                        </td>
                        <td className='text-center align-middle'>
                          <span className='fw-bold text-success'>{Number(item.remainingAmount).toLocaleString()}</span>
                        </td>
                        <td className='text-center align-middle'>
                          {isSelected ? (
                            <div className='d-flex align-items-center gap-2 justify-content-center'>
                              <input
                                type='text'
                                className='form-control form-control-sm text-center'
                                value={receiptAmount}
                                onChange={e => handleAmountChange(item.arReceiptScheduleId, e.target.value)}
                                style={{ width: '120px' }}
                                onInput={e => {
                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value.replace(/[^0-9.]/g, '');

                                  if (input.value.split('.').length > 2) {
                                    input.value = input.value.substring(0, input.value.length - 1);
                                  }

                                  if (parseFloat(input.value) > item.remainingAmount) {
                                    input.value = item.remainingAmount.toString();
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                        <td className='text-center align-middle'>{item.dueDate?.toString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-between align-items-center'>
            <div className='text-muted small'>
              عرض {sortedTransactions.length} من {sortedTransactions.length} فاتورة
            </div>
            <div className='text-theme fw-bold'>المبلغ الإجمالي المحدد: {totalSelectedAmount.toLocaleString()}</div>
          </div>
        </Card>
      )}

      {hasValidationErrors && (
        <div className='alert alert-warning d-flex align-items-center mb-4' role='alert'>
          <i className='fas fa-exclamation-triangle me-2'></i>
          <div>يجب إدخال مبلغ أكبر من الصفر للفواتير المحددة</div>
        </div>
      )}

      <div className='border-top mt-4 pt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            {isFormValid && (
              <div className='alert alert-success d-flex align-items-center py-2 mb-0' role='alert'>
                <i className='fas fa-check-circle me-2'></i>
                <div>جاهز لدفع الفواتير المحددة</div>
              </div>
            )}
          </div>

          <div className='d-flex'>
            <button disabled={!isFormValid} className='btn btn-theme me-2 px-4' onClick={handleSubmit}>
              <i className='fas fa-money-bill me-2'></i>
              دفع الفواتير المحددة
            </button>

            <button
              type='button'
              className='btn btn-outline-secondary'
              onClick={() => navigate('/AR/ArReceipts', { replace: true })}
            >
              <i className='fas fa-times me-2'></i>
              إلغاء
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

////////////////////////////////////////////////////////

interface PrepaymentsAlertProps {
  peopleId: string;
}

export const PrepaymentsAlert: React.FC<PrepaymentsAlertProps> = ({ peopleId }) => {
  const { t } = useTranslation();

  // استخدام الهوك داخل المكون
  const {
    hasPrepayments,
    totalPrepaymentAmount,
    UnApplyPrepaymentsList,
    loading: prepaymentsLoading,
    error: prepaymentsError
  } = useGetUnApplyPrepayments({
    PeopleId: peopleId
  });

  if (!peopleId) {
    return null;
  }

  return (
    <div>
      <Loading loading={prepaymentsLoading} error={prepaymentsError} Type='Dots'>
        {hasPrepayments && (
          <div className='alert alert-info d-flex align-items-center' role='alert'>
            <i className='fas fa-info-circle me-2 fs-5 text-info'></i>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center flex-wrap'>
                <span className='me-2'>
                  <strong>{t('Reminder')} : </strong>
                  {t('PrepaymentMessage')}
                </span>
                <span className='fw-bold text-success'>
                  {/* {totalPrepaymentAmount?.toLocaleString() || 0} {t('Currency')} */}
                  {totalPrepaymentAmount?.toLocaleString() || 0}
                </span>

                {/* {UnApplyPrepaymentsList && UnApplyPrepaymentsList.length > 0 && (
                  <span className='badge bg-warning text-dark ms-2'>
                    {UnApplyPrepaymentsList.length} {t('Prepayments')}
                  </span>
                )} */}
              </div>
            </div>
          </div>
        )}
      </Loading>
    </div>
  );
};
////////////////////////////////////////////////////////
